import { Injectable } from '@angular/core';
import { TourService, IStepOption } from 'ngx-ui-tour-md-menu';
import { Subscription } from 'rxjs';

import { initializeFeatureTour } from './tour-initializer';
import { TourConfigService } from './tour-config.service';
import { FeatureTourStep, FeatureTourStepContent } from './tour.types';
import { QuickGuideVisibilityService } from '../../../../shared/quick-guide/quick-guide-visibility.service';
const TOUR_STATE_ON = 1;
const POPOVER_VIEWPORT_MARGIN = 16;
const POPOVER_DOCK_MARGIN = 12;
const POPOVER_ANCHOR_GAP = 14;
const POPOVER_MAX_ANCHOR_DISTANCE = 64;
const POPOVER_OFFSET_RETRY_LIMIT = 30;
const SHORT_VIEWPORT_HEIGHT = 720;

interface PopoverPosition {
  x: number;
  y: number;
}

interface PopoverCandidate {
  name: 'right' | 'left' | 'bottom' | 'top';
  left: number;
  top: number;
  priority: number;
  score?: number;
}

export interface FeatureTourContext {
  closeColumnFilterMenu: () => void;
  closeRowActionsMenu: () => void;
  openFirstRowActionsMenu: () => void;
  selectFirstEmployees: (count: number) => void;
  scrollSelectionActionsIntoView: () => void;
}

export type EmployeesListTourContext = FeatureTourContext;

const DEFAULT_CONTEXT: FeatureTourContext = {
  closeColumnFilterMenu: () => undefined,
  closeRowActionsMenu: () => undefined,
  openFirstRowActionsMenu: () => undefined,
  selectFirstEmployees: () => undefined,
  scrollSelectionActionsIntoView: () => undefined,
};

@Injectable({ providedIn: 'root' })
export class FeatureTourService {
  private tourStepSubscription?: Subscription;
  private tourEndSubscription?: Subscription;
  private context: FeatureTourContext = DEFAULT_CONTEXT;

  constructor(
    private readonly tourService: TourService<FeatureTourStep>,
    private readonly tourConfigService: TourConfigService,
    private readonly quickGuideVisibilityService: QuickGuideVisibilityService,
  ) {}

  startScenarioById(scenarioId: string, startAtStep?: number | string): void {
    if (!this.quickGuideVisibilityService.isEnabled()) {
      return;
    }

    const scenario = this.tourConfigService.getScenarioById(scenarioId);

    if (!scenario?.steps?.length) {
      return;
    }

    this.startScenario(scenario.steps, startAtStep);
  }

  registerContext(partialContext: Partial<FeatureTourContext>): void {
    this.context = {
      ...DEFAULT_CONTEXT,
      ...partialContext,
    };
  }

  clearContext(): void {
    this.context = DEFAULT_CONTEXT;
  }

  dispose(): void {
    this.resetSubscriptions();
    this.clearContext();
  }

  private startScenario(steps: FeatureTourStep[], startAtStep?: number | string): void {
    const clonedSteps = steps
      .map((step) => this.cloneStep(step))
      .map((step, index) => (this.matchesTargetStep(step, index, startAtStep) ? this.withAsyncInit(step) : step));

    if ((this.tourService.getStatus() as number) === TOUR_STATE_ON) {
      this.tourService.end();
    }

    this.initializeTour(clonedSteps);
    const initialStep = this.getInitialStep(clonedSteps, startAtStep);

    if (startAtStep !== undefined && startAtStep !== null) {
      this.tourService.startAt(startAtStep);
      this.scheduleInitialPopoverOffset(initialStep);
      return;
    }

    this.tourService.start();
    this.scheduleInitialPopoverOffset(initialStep);
  }

  private initializeTour(steps: FeatureTourStep[]): void {
    this.resetSubscriptions();

    const { stepSubscription, endSubscription } = initializeFeatureTour(
      this.tourService,
      steps,
      {
        onStepShow: (step) => this.handleTourStep(step),
        onTourEnd: () => this.handleTourEnd(),
      },
    );

    this.tourStepSubscription = stepSubscription;
    this.tourEndSubscription = endSubscription;
  }

  private handleTourStep(step?: IStepOption): void {
    const anchorId = step?.anchorId;

    if (!anchorId) {
      return;
    }

    if (anchorId !== 'customise-columns') {
      this.context.closeColumnFilterMenu();
    }

    if (anchorId !== 'employee-actions') {
      this.context.closeRowActionsMenu();
    }

    switch (anchorId) {
      case 'employee-actions':
        setTimeout(() => this.context.openFirstRowActionsMenu());
        break;
      case 'employee-checkbox':
        setTimeout(() => this.context.selectFirstEmployees(2));
        break;
      case 'selection-actions':
        setTimeout(() => {
          this.context.selectFirstEmployees(2);
          this.context.scrollSelectionActionsIntoView();
        });
        break;
      default:
        break;
    }

    this.schedulePopoverOffset(step as FeatureTourStep);
  }

  private handleTourEnd(): void {
    this.context.closeColumnFilterMenu();
    this.context.closeRowActionsMenu();
  }

  private cloneStep(step: FeatureTourStep): FeatureTourStep {
    return {
      ...step,
      offset: step?.offset ? { ...step.offset } : step?.offset,
      media: step?.media ? { ...step.media } : step?.media,
      backdropConfig: step?.backdropConfig ? { ...step.backdropConfig } : step?.backdropConfig,
      stepDimensions: step?.stepDimensions ? { ...step.stepDimensions } : step?.stepDimensions,
      placement: step?.placement ? { ...step.placement } : step?.placement,
      route: Array.isArray(step.route) ? [...step.route] : step.route,
      keywords: step?.keywords ? [...step.keywords] : step?.keywords,
      richContent: this.cloneRichContent(step.richContent),
    };
  }

  private cloneRichContent(content: FeatureTourStepContent | undefined): FeatureTourStepContent | undefined {
    if (!content) {
      return undefined;
    }

    return {
      shortDescription: content.shortDescription,
      longDescription: content.longDescription,
      impactOnSystem: content.impactOnSystem,
      risks: content.risks,
    };
  }

  private matchesTargetStep(step: FeatureTourStep, index: number, target?: number | string): boolean {
    if (target === undefined || target === null) {
      return false;
    }

    if (typeof target === 'number') {
      return index === target;
    }

    if (step.stepId && step.stepId === target) {
      return true;
    }

    return step.anchorId === target;
  }

  private getInitialStep(steps: FeatureTourStep[], startAtStep?: number | string): FeatureTourStep | undefined {
    if (startAtStep === undefined || startAtStep === null) {
      return steps[0];
    }

    return steps.find((step, index) => this.matchesTargetStep(step, index, startAtStep));
  }

  private withAsyncInit(step: FeatureTourStep): FeatureTourStep {
    if (step.isAsync) {
      return step;
    }

    return {
      ...step,
      isAsync: true,
      asyncStepTimeout: step.asyncStepTimeout ?? 10000,
      delayAfterNavigation: step.delayAfterNavigation ?? 400,
    };
  }

  private schedulePopoverOffset(step?: FeatureTourStep): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.requestAnimationFrame(() => this.applyPopoverOffset(step));
    window.setTimeout(() => this.applyPopoverOffset(step), 80);
    window.setTimeout(() => this.applyPopoverOffset(step), 180);
  }

  private scheduleInitialPopoverOffset(step?: FeatureTourStep): void {
    if (typeof window === 'undefined') {
      return;
    }

    const delay = Math.max(250, (step?.delayAfterNavigation ?? 0) + 250);
    window.setTimeout(() => this.applyPopoverOffset(step), delay);
    window.setTimeout(() => this.applyPopoverOffset(step), delay + 300);
  }

  private applyPopoverOffset(step?: FeatureTourStep, attempt = 0): void {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }

    const pane = this.findTourOverlayPane();

    if (!pane) {
      if (attempt < POPOVER_OFFSET_RETRY_LIMIT) {
        window.setTimeout(() => this.applyPopoverOffset(step, attempt + 1), 32);
      }
      return;
    }

    const anchor = this.findAnchorElement(step?.anchorId);

    if (anchor && this.scrollAnchorIntoSafeViewOnce(pane, anchor, step, attempt)) {
      return;
    }

    const anchorKey = step?.anchorId ?? '';
    const computedTransform = window.getComputedStyle(pane).transform;
    const normalizedTransform = computedTransform === 'none' ? '' : computedTransform;
    const isSameAnchor = pane.dataset['tourAnchorId'] === anchorKey;
    const baseTransform = isSameAnchor && pane.dataset['tourBaseTransform'] !== undefined
      ? pane.dataset['tourBaseTransform']
      : normalizedTransform;

    if (!isSameAnchor) {
      pane.dataset['tourAnchorId'] = anchorKey;
      pane.dataset['tourBaseTransform'] = baseTransform;
    }

    const hasOffset = step?.offset && (step.offset.x !== undefined || step.offset.y !== undefined);

    const baseTranslate = this.extractTranslate(baseTransform);
    const offsetX = hasOffset ? step?.offset?.x ?? 0 : 0;
    const offsetY = hasOffset ? step?.offset?.y ?? 0 : 0;

    const targetX = baseTranslate.x + offsetX;
    const targetY = baseTranslate.y + offsetY;

    this.setCompactMode(pane, false);
    this.setDockedMode(pane, false);
    this.setPopoverTransform(pane, baseTransform, targetX, targetY);
    const clampedPosition = this.clampPopoverToViewport(pane, baseTransform, targetX, targetY);
    this.keepAnchorVisible(pane, anchor, baseTransform, clampedPosition);
    this.syncPopoverArrow(pane, anchor);
  }

  private clampPopoverToViewport(
    pane: HTMLElement,
    baseTransform: string,
    targetX: number,
    targetY: number,
  ): PopoverPosition {
    const rect = pane.getBoundingClientRect();
    let correctionX = 0;
    let correctionY = 0;

    if (rect.left < POPOVER_VIEWPORT_MARGIN) {
      correctionX = POPOVER_VIEWPORT_MARGIN - rect.left;
    } else if (rect.right > window.innerWidth - POPOVER_VIEWPORT_MARGIN) {
      correctionX = window.innerWidth - POPOVER_VIEWPORT_MARGIN - rect.right;
    }

    if (rect.top < POPOVER_VIEWPORT_MARGIN) {
      correctionY = POPOVER_VIEWPORT_MARGIN - rect.top;
    } else if (rect.bottom > window.innerHeight - POPOVER_VIEWPORT_MARGIN) {
      correctionY = window.innerHeight - POPOVER_VIEWPORT_MARGIN - rect.bottom;
    }

    if (correctionX === 0 && correctionY === 0) {
      return { x: targetX, y: targetY };
    }

    const nextPosition = { x: targetX + correctionX, y: targetY + correctionY };
    this.setPopoverTransform(pane, baseTransform, nextPosition.x, nextPosition.y);

    return nextPosition;
  }

  private keepAnchorVisible(
    pane: HTMLElement,
    anchor: HTMLElement | null,
    baseTransform: string,
    currentPosition: PopoverPosition,
  ): void {
    if (!anchor) {
      return;
    }

    const anchorRect = anchor.getBoundingClientRect();
    const paneRect = pane.getBoundingClientRect();

    if (!this.hasUsableRect(anchorRect) || !this.hasUsableRect(paneRect)) {
      return;
    }

    if (
      !this.rectsOverlap(paneRect, anchorRect, POPOVER_ANCHOR_GAP)
      && this.getRectDistance(paneRect, anchorRect) <= POPOVER_MAX_ANCHOR_DISTANCE
    ) {
      return;
    }

    if (this.moveToSafeAnchorCandidate(pane, baseTransform, currentPosition, paneRect, anchorRect)) {
      return;
    }

    this.setCompactMode(pane, true);
    const compactPaneRect = pane.getBoundingClientRect();

    if (this.moveToSafeAnchorCandidate(pane, baseTransform, currentPosition, compactPaneRect, anchorRect)) {
      return;
    }

    this.setCompactMode(pane, false);
    this.dockPopoverAwayFromAnchor(pane, baseTransform, currentPosition, anchorRect);
  }

  private scrollAnchorIntoSafeViewOnce(
    pane: HTMLElement,
    anchor: HTMLElement,
    step: FeatureTourStep | undefined,
    attempt: number,
  ): boolean {
    const anchorRect = anchor.getBoundingClientRect();

    if (!this.hasUsableRect(anchorRect)) {
      return false;
    }

    const needsShortViewportScroll = window.innerHeight < SHORT_VIEWPORT_HEIGHT;
    const isTooCloseToViewportEdge = anchorRect.top < 96 || anchorRect.bottom > window.innerHeight - 96;

    if (!needsShortViewportScroll && !isTooCloseToViewportEdge) {
      return false;
    }

    const anchorKey = step?.anchorId ?? '';
    const scrollKey = `${anchorKey}:${window.innerWidth}x${window.innerHeight}`;

    if (pane.dataset['tourAnchorScrollKey'] === scrollKey || attempt > 0) {
      return false;
    }

    pane.dataset['tourAnchorScrollKey'] = scrollKey;
    anchor.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
    window.setTimeout(() => this.applyPopoverOffset(step, attempt + 1), 80);
    window.setTimeout(() => this.applyPopoverOffset(step, attempt + 1), 180);
    window.setTimeout(() => this.applyPopoverOffset(step, attempt + 1), 320);
    window.setTimeout(() => this.applyPopoverOffset(step, attempt + 1), 800);
    window.setTimeout(() => this.applyPopoverOffset(step, attempt + 1), 1400);

    return true;
  }

  private pickAnchorAwareCandidate(anchorRect: DOMRect, paneRect: DOMRect): PopoverCandidate | undefined {
    const candidates = this.buildAnchorAwareCandidates(anchorRect, paneRect)
      .map((candidate) => ({
        ...candidate,
        score: this.scoreCandidate(candidate, paneRect, anchorRect),
      }))
      .sort((a, b) => (a.score ?? 0) - (b.score ?? 0));

    return candidates[0];
  }

  private moveToSafeAnchorCandidate(
    pane: HTMLElement,
    baseTransform: string,
    currentPosition: PopoverPosition,
    paneRect: DOMRect,
    anchorRect: DOMRect,
  ): boolean {
    const bestCandidate = this.pickAnchorAwareCandidate(anchorRect, paneRect);

    if (
      !bestCandidate ||
      this.getCandidateOverlapArea(bestCandidate, paneRect, anchorRect) !== 0 ||
      this.getCandidateOutsideViewportPenalty(bestCandidate, paneRect) !== 0
    ) {
      return false;
    }

    this.movePopoverTo(pane, baseTransform, currentPosition, bestCandidate.left, bestCandidate.top);

    return true;
  }

  private buildAnchorAwareCandidates(anchorRect: DOMRect, paneRect: DOMRect): PopoverCandidate[] {
    const centeredTop = anchorRect.top + (anchorRect.height / 2) - (paneRect.height / 2);
    const centeredLeft = anchorRect.left + (anchorRect.width / 2) - (paneRect.width / 2);

    return [
      {
        name: 'right',
        left: anchorRect.right + POPOVER_ANCHOR_GAP,
        top: this.clampToViewport(centeredTop, paneRect.height, window.innerHeight, POPOVER_VIEWPORT_MARGIN),
        priority: 1,
      },
      {
        name: 'left',
        left: anchorRect.left - paneRect.width - POPOVER_ANCHOR_GAP,
        top: this.clampToViewport(centeredTop, paneRect.height, window.innerHeight, POPOVER_VIEWPORT_MARGIN),
        priority: 2,
      },
      {
        name: 'bottom',
        left: this.clampToViewport(centeredLeft, paneRect.width, window.innerWidth, POPOVER_VIEWPORT_MARGIN),
        top: this.clampBelowAnchor(anchorRect.bottom + POPOVER_ANCHOR_GAP, paneRect.height, anchorRect.bottom),
        priority: 3,
      },
      {
        name: 'top',
        left: this.clampToViewport(centeredLeft, paneRect.width, window.innerWidth, POPOVER_VIEWPORT_MARGIN),
        top: this.clampAboveAnchor(anchorRect.top - paneRect.height - POPOVER_ANCHOR_GAP, paneRect.height, anchorRect.top),
        priority: 4,
      },
    ];
  }

  private scoreCandidate(candidate: PopoverCandidate, paneRect: DOMRect, anchorRect: DOMRect): number {
    const candidateRect = this.toRect(candidate.left, candidate.top, paneRect.width, paneRect.height);
    const outsidePenalty = this.getOutsideViewportPenalty(candidateRect);
    const overlapPenalty = this.getOverlapArea(candidateRect, anchorRect) * 100;

    return overlapPenalty + (outsidePenalty * 10) + candidate.priority;
  }

  private getCandidateOverlapArea(candidate: PopoverCandidate, paneRect: DOMRect, anchorRect: DOMRect): number {
    const candidateRect = this.toRect(candidate.left, candidate.top, paneRect.width, paneRect.height);

    return this.getOverlapArea(candidateRect, anchorRect);
  }

  private getCandidateOutsideViewportPenalty(candidate: PopoverCandidate, paneRect: DOMRect): number {
    const candidateRect = this.toRect(candidate.left, candidate.top, paneRect.width, paneRect.height);

    return this.getOutsideViewportPenalty(candidateRect);
  }

  private dockPopoverAwayFromAnchor(
    pane: HTMLElement,
    baseTransform: string,
    currentPosition: PopoverPosition,
    anchorRect: DOMRect,
  ): void {
    const roomAbove = anchorRect.top - POPOVER_ANCHOR_GAP - POPOVER_DOCK_MARGIN;
    const roomBelow = window.innerHeight - anchorRect.bottom - POPOVER_ANCHOR_GAP - POPOVER_DOCK_MARGIN;
    const dockAtTop = roomAbove >= roomBelow;
    const availableRoom = Math.floor((dockAtTop ? roomAbove : roomBelow) - 10);
    const maxHeight = Math.max(150, Math.min(420, availableRoom));

    this.setDockedMode(pane, true);
    pane.dataset['tourDockPlacement'] = dockAtTop ? 'top' : 'bottom';
    pane.style.setProperty('--tour-step-docked-max-height', `${maxHeight}px`);

    const dockedRect = pane.getBoundingClientRect();
    const desiredLeft = Math.max(POPOVER_DOCK_MARGIN, (window.innerWidth - dockedRect.width) / 2);
    const desiredTop = dockAtTop
      ? this.getDockedTopAboveAnchor(anchorRect, dockedRect.height)
      : this.getDockedTopBelowAnchor(anchorRect, dockedRect.height);

    this.movePopoverTo(pane, baseTransform, currentPosition, desiredLeft, desiredTop);
  }

  private getDockedTopAboveAnchor(anchorRect: DOMRect, dockedHeight: number): number {
    return Math.max(
      POPOVER_DOCK_MARGIN,
      anchorRect.top - dockedHeight - POPOVER_ANCHOR_GAP,
    );
  }

  private getDockedTopBelowAnchor(anchorRect: DOMRect, dockedHeight: number): number {
    const maxTop = Math.max(
      POPOVER_DOCK_MARGIN,
      window.innerHeight - dockedHeight - POPOVER_DOCK_MARGIN,
    );
    const desiredTop = anchorRect.bottom + POPOVER_ANCHOR_GAP;

    return Math.max(anchorRect.bottom, Math.min(desiredTop, maxTop));
  }

  private movePopoverTo(
    pane: HTMLElement,
    baseTransform: string,
    currentPosition: PopoverPosition,
    desiredLeft: number,
    desiredTop: number,
  ): PopoverPosition {
    const rect = pane.getBoundingClientRect();
    const nextPosition = {
      x: currentPosition.x + desiredLeft - rect.left,
      y: currentPosition.y + desiredTop - rect.top,
    };

    this.setPopoverTransform(pane, baseTransform, nextPosition.x, nextPosition.y);

    return nextPosition;
  }

  private setDockedMode(pane: HTMLElement, isDocked: boolean): void {
    pane.classList.toggle('tour-step-overlay--docked', isDocked);

    if (!isDocked) {
      delete pane.dataset['tourDockPlacement'];
      pane.style.removeProperty('--tour-step-docked-max-height');
    }
  }

  private setCompactMode(pane: HTMLElement, isCompact: boolean): void {
    pane.classList.toggle('tour-step-overlay--compact', isCompact);
  }

  private syncPopoverArrow(pane: HTMLElement, anchor: HTMLElement | null): void {
    const panel = this.findTourPanel(pane);

    if (!panel) {
      return;
    }

    panel.classList.add('tour-step-arrow');
    panel.classList.remove(
      'tour-step-arrow--hidden',
      'tour-step-arrow--top',
      'tour-step-arrow--right',
      'tour-step-arrow--bottom',
      'tour-step-arrow--left',
    );

    if (!anchor) {
      panel.classList.add('tour-step-arrow--hidden');
      return;
    }

    const anchorRect = anchor.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();

    if (!this.hasUsableRect(anchorRect) || !this.hasUsableRect(panelRect)) {
      panel.classList.add('tour-step-arrow--hidden');
      return;
    }

    const anchorCenter = {
      x: anchorRect.left + (anchorRect.width / 2),
      y: anchorRect.top + (anchorRect.height / 2),
    };
    const side = this.getArrowSide(anchorCenter, panelRect);
    const arrowX = this.clamp(anchorCenter.x - panelRect.left, 28, panelRect.width - 28);
    const arrowY = this.clamp(anchorCenter.y - panelRect.top, 28, panelRect.height - 28);

    panel.classList.add(`tour-step-arrow--${side}`);
    panel.style.setProperty('--tour-step-arrow-x', `${arrowX}px`);
    panel.style.setProperty('--tour-step-arrow-y', `${arrowY}px`);
  }

  private setPopoverTransform(pane: HTMLElement, baseTransform: string, x: number, y: number): void {
    pane.style.transform = this.buildTransformWithOffset(baseTransform, x, y);
  }

  private findTourOverlayPane(): HTMLElement | null {
    if (typeof document === 'undefined') {
      return null;
    }

    const panel = document.querySelector<HTMLElement>('.mat-mdc-menu-panel.tour-step');
    return panel?.parentElement ?? null;
  }

  private findTourPanel(pane: HTMLElement): HTMLElement | null {
    return pane.querySelector<HTMLElement>('.mat-mdc-menu-panel.tour-step');
  }

  private findAnchorElement(anchorId?: string): HTMLElement | null {
    if (!anchorId || typeof document === 'undefined') {
      return null;
    }

    const escapedAnchorId = this.escapeCssIdentifier(anchorId);

    return document.querySelector<HTMLElement>(
      `[tourAnchor="${escapedAnchorId}"], [touranchor="${escapedAnchorId}"], [data-tour-anchor="${escapedAnchorId}"]`,
    );
  }

  private escapeCssIdentifier(value: string): string {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
      return CSS.escape(value);
    }

    return value.replace(/["\\]/g, '\\$&');
  }

  private hasUsableRect(rect: DOMRect): boolean {
    return rect.width > 0 && rect.height > 0;
  }

  private rectsOverlap(a: DOMRect, b: DOMRect, gap = 0): boolean {
    return !(
      a.right + gap <= b.left ||
      a.left - gap >= b.right ||
      a.bottom + gap <= b.top ||
      a.top - gap >= b.bottom
    );
  }

  private getOverlapArea(a: DOMRect, b: DOMRect): number {
    const overlapWidth = Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left));
    const overlapHeight = Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));

    return overlapWidth * overlapHeight;
  }

  private getRectDistance(a: DOMRect, b: DOMRect): number {
    const horizontalGap = Math.max(0, b.left - a.right, a.left - b.right);
    const verticalGap = Math.max(0, b.top - a.bottom, a.top - b.bottom);

    return Math.max(horizontalGap, verticalGap);
  }

  private getOutsideViewportPenalty(rect: DOMRect): number {
    return Math.max(0, POPOVER_VIEWPORT_MARGIN - rect.left)
      + Math.max(0, rect.right - (window.innerWidth - POPOVER_VIEWPORT_MARGIN))
      + Math.max(0, POPOVER_VIEWPORT_MARGIN - rect.top)
      + Math.max(0, rect.bottom - (window.innerHeight - POPOVER_VIEWPORT_MARGIN));
  }

  private toRect(left: number, top: number, width: number, height: number): DOMRect {
    return {
      bottom: top + height,
      height,
      left,
      right: left + width,
      top,
      width,
      x: left,
      y: top,
      toJSON: () => ({}),
    } as DOMRect;
  }

  private clampToViewport(value: number, size: number, viewportSize: number, margin: number): number {
    const max = Math.max(margin, viewportSize - size - margin);

    return Math.min(Math.max(value, margin), max);
  }

  private clampBelowAnchor(desiredTop: number, height: number, anchorBottom: number): number {
    const maxTop = window.innerHeight - height - POPOVER_VIEWPORT_MARGIN;

    return Math.max(anchorBottom, Math.min(desiredTop, maxTop));
  }

  private clampAboveAnchor(desiredTop: number, height: number, anchorTop: number): number {
    const minTop = POPOVER_VIEWPORT_MARGIN;
    const maxTop = anchorTop - height;

    return Math.min(maxTop, Math.max(desiredTop, minTop));
  }

  private clamp(value: number, min: number, max: number): number {
    if (max < min) {
      return (min + max) / 2;
    }

    return Math.min(Math.max(value, min), max);
  }

  private getArrowSide(anchorCenter: { x: number; y: number }, panelRect: DOMRect): 'top' | 'right' | 'bottom' | 'left' {
    if (anchorCenter.y < panelRect.top) {
      return 'top';
    }

    if (anchorCenter.y > panelRect.bottom) {
      return 'bottom';
    }

    if (anchorCenter.x < panelRect.left) {
      return 'left';
    }

    if (anchorCenter.x > panelRect.right) {
      return 'right';
    }

    const distances = [
      { side: 'top' as const, value: Math.abs(anchorCenter.y - panelRect.top) },
      { side: 'right' as const, value: Math.abs(anchorCenter.x - panelRect.right) },
      { side: 'bottom' as const, value: Math.abs(anchorCenter.y - panelRect.bottom) },
      { side: 'left' as const, value: Math.abs(anchorCenter.x - panelRect.left) },
    ].sort((a, b) => a.value - b.value);

    return distances[0].side;
  }

  private extractTranslate(transform: string): { x: number; y: number } {
    if (!transform || transform === 'none') {
      return { x: 0, y: 0 };
    }

    const matrix3dMatch = transform.match(/matrix3d\(([^)]+)\)/);
    if (matrix3dMatch) {
      const values = matrix3dMatch[1].split(',').map((value) => Number(value.trim()));
      return { x: values[12] ?? 0, y: values[13] ?? 0 };
    }

    const matrixMatch = transform.match(/matrix\(([^)]+)\)/);
    if (matrixMatch) {
      const values = matrixMatch[1].split(',').map((value) => Number(value.trim()));
      return { x: values[4] ?? 0, y: values[5] ?? 0 };
    }

    const translateMatch = transform.match(/translate(?:3d)?\(([^)]+)\)/);
    if (translateMatch) {
      const values = translateMatch[1].split(',').map((value) => parseFloat(value.trim().replace('px', '')) || 0);
      return { x: values[0], y: values[1] };
    }

    return { x: 0, y: 0 };
  }

  private buildTransformWithOffset(baseTransform: string, x: number, y: number): string {
    if (!baseTransform || baseTransform === 'none') {
      return `translate3d(${x}px, ${y}px, 0px)`;
    }

    const matrix3dMatch = baseTransform.match(/matrix3d\(([^)]+)\)/);
    if (matrix3dMatch) {
      const values = matrix3dMatch[1].split(',').map((value) => Number(value.trim()));
      values[12] = x;
      values[13] = y;
      return `matrix3d(${values.join(', ')})`;
    }

    const matrixMatch = baseTransform.match(/matrix\(([^)]+)\)/);
    if (matrixMatch) {
      const values = matrixMatch[1].split(',').map((value) => Number(value.trim()));
      values[4] = x;
      values[5] = y;
      return `matrix(${values.join(', ')})`;
    }

    const translate3dMatch = baseTransform.match(/translate3d\(([^)]+)\)/);
    if (translate3dMatch) {
      const parts = translate3dMatch[1].split(',');
      parts[0] = `${x}px`;
      parts[1] = `${y}px`;
      return baseTransform.replace(translate3dMatch[0], `translate3d(${parts.join(',')})`);
    }

    const translateMatch = baseTransform.match(/translate\(([^)]+)\)/);
    if (translateMatch) {
      const parts = translateMatch[1].split(',');
      parts[0] = `${x}px`;
      parts[1] = `${y}px`;
      return baseTransform.replace(translateMatch[0], `translate(${parts.join(',')})`);
    }

    return `translate3d(${x}px, ${y}px, 0px) ${baseTransform}`.trim();
  }

  private resetSubscriptions(): void {
    this.tourStepSubscription?.unsubscribe();
    this.tourEndSubscription?.unsubscribe();
    this.tourStepSubscription = undefined;
    this.tourEndSubscription = undefined;
  }
}

export { FeatureTourService as EmployeesListTourService };
